import { type ReactNode, type ReactElement, forwardRef, type Ref } from 'react';

import type {
  DataTestId,
  StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * @public
 */
export interface DataTableV2SelectedRowsActionsProps<
  TData extends DataTableV2RowData,
> extends StylingProps,
    DataTestId {
  children: (
    rows: {
      rowId: string;
      originalRow: TData;
    }[],
  ) => ReactNode;
}

// A helper function to check whether the child component is the SelectedRowsActions component
/**
 * @internal
 */
export function _isDataTableV2SelectedRowsActions<
  TData extends DataTableV2RowData,
>(
  child: ReactElement | null,
): child is ReactElement<DataTableV2SelectedRowsActionsProps<TData>> {
  return child?.type === DataTableV2SelectedRowsActions;
}

/**
 * Selected rows actions component used for configuring actions
 * that can be applied on one or multiple rows in the DataTable simultaneously.
 * Also, it gets called by DataTableV2 as rows get selected/unselected.
 *
 * @param props - contains children or self
 *
 * @returns DataTableV2SelectedRowsActions containing children.
 * @public
 */
export const DataTableV2SelectedRowsActions = forwardRef(
  <TData extends DataTableV2RowData>(
    props: DataTableV2SelectedRowsActionsProps<TData>,
    forwardedRef: Ref<HTMLDivElement>,
  ) => {
    const table = useDataTableV2();

    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    if (typeof children !== 'function') {
      // eslint-disable-next-line no-console
      console.warn(
        'The SelectedRowsActions sub-component child must be a function!',
      );
      return null;
    }

    const currentSelectableRows = table
      .getSelectedRowModel()
      .flatRows.map((row) => ({
        rowId: row.id,
        originalRow: row.original as TData,
      }));

    return (
      <Flex
        ref={forwardedRef}
        justifyContent="flex-start"
        alignItems="flex-end"
        flexWrap="wrap"
        data-testid={dataTestId}
        className={consumerClassName}
        style={consumerStyle}
        {...remainingProps}
      >
        <Flex alignItems={'center'} flexWrap={'wrap'} gap={4}>
          {children(currentSelectableRows)}
        </Flex>
      </Flex>
    );
  },
);

(
  DataTableV2SelectedRowsActions as typeof DataTableV2SelectedRowsActions & {
    displayName: string;
  }
).displayName = 'DataTableV2.SelectedRowsActions';
